import {
  type MeasurementKind,
  type PreferenceValues,
  UNIT_CODES,
  type UnitCodeFor,
} from '../contract/types';
import { convert } from '../units/convert';
import { labelFor } from '../units/labels';
import { parseNumber } from '../units/parse';
import { digitsFor, type PrecisionContext } from '../units/precision';
import {
  type AddressFields,
  formatAddress,
  formatAddressLines,
} from './address';
import {
  type CurrencyFacts,
  type CurrencyFactsOptions,
  currencyFactsOf,
} from './currency';
import {
  type DateFormatOptions,
  type DateTimeOptions,
  datePatternOf,
  formatCalendarTime,
  formatClockDuration,
  formatDate,
  formatDateLong,
  formatDateRange,
  formatDateTime,
  formatDuration,
  formatIsoDuration,
  formatPlainDate,
  formatPlainDateLong,
  formatPlainDateRange,
  formatPlainMonthDay,
  formatPlainTime,
  formatRecency,
  formatRelativeTime,
  formatTime,
  formatTimeRange,
  formatZoneName,
  type IsoDurationOptions,
} from './datetime';
import { formatMeasurement } from './measurement';
import { formatMoney, type MoneyFormatOptions } from './money';
import { formatNumber, type NumberFormatOptions } from './number';
import { formatPercent, type PercentFormatOptions } from './percent';
import { formatPhone } from './phone';

/**
 * The object a feature developer actually holds.
 *
 * Every entry is already bound to the user's preferences, which is the point: a caller says what
 * unit the *backend* sent and never says what unit the *user* wants, because that question has one
 * answer for the whole session and asking it at each call site is how conditionals spread through a
 * codebase.
 *
 * The bundle is built once per preference set and handed out by reference, so it is safe in a
 * dependency array. Nothing here closes over anything that changes between renders.
 */

export type MeasurementFormatter<K extends MeasurementKind> = {
  /** The unit this quantity is shown in. Chart axes and column headers need it. */
  readonly unit: UnitCodeFor<K>;
  /**
   * The value converted, rounded and labelled — the string a reader sees.
   *
   * @param value - The number as the backend sent it.
   * @param options.valueUnit - The unit the backend sent it in.
   * @param options.context - Where it is being shown, which decides the precision.
   */
  readonly format: (
    value: number,
    options: { valueUnit: UnitCodeFor<K>; context?: PrecisionContext }
  ) => string;
  /**
   * The value in the user's unit, unrounded and unformatted, for arithmetic, sorting and axes.
   *
   * @param value - The number as the backend sent it.
   * @param options.valueUnit - The unit the backend sent it in.
   */
  readonly raw: (
    value: number,
    options: { valueUnit: UnitCodeFor<K> }
  ) => number;
  /** The unit's short label on its own, for a column header or a chart axis. */
  readonly label: () => string;
  /** Fractional digits appropriate to this quantity in the user's unit. */
  readonly digits: (context?: PrecisionContext) => number;
};

/**
 * Reads a string the user typed and hands back the unit an endpoint expects.
 *
 * The input is read in the user's unit, because that is what the field they typed into was
 * labelled with, and converted into `to`. `undefined` when the string does not unambiguously hold
 * a number, so a form can reject it rather than submit a guess.
 */
export type MeasurementParser<K extends MeasurementKind> = (
  input: string,
  options: { to: UnitCodeFor<K> }
) => number | undefined;

export type ParseBundle = {
  readonly [K in MeasurementKind]: MeasurementParser<K>;
} & {
  /** A bare number in the user's locale, with no unit involved. */
  readonly number: (input: string) => number | undefined;
};

export type FormatBundle = {
  readonly [K in MeasurementKind]: MeasurementFormatter<K>;
} & {
  readonly number: (value: number, options?: NumberFormatOptions) => string;
  /**
   * A percent point, so `18.632` renders as `18.63%`.
   *
   * The ceiling is two fractional digits unless the caller sets
   * {@link PercentFormatOptions.maximumDigits}. Trailing zeros are dropped. A
   * fraction belongs on {@link FormatBundle.number} with `notation: 'percent'`.
   */
  readonly percent: (value: number, options?: PercentFormatOptions) => string;
  /** An amount of money. Returns `''` unless the caller passes a code from `SUPPORTED_CURRENCY_CODES`. */
  readonly money: (value: number, options?: MoneyFormatOptions) => string;
  /**
   * What a currency looks like to this reader, for a **field they type an amount into** — the
   * symbol, the side it sits on, the spacing, and the decimals the currency carries.
   *
   * Anything that merely shows an amount wants {@link FormatBundle.money} or `<Money>`, which
   * assemble these into the one string. An input has to lay the symbol out beside the digits
   * itself, and a leading `$` is wrong wherever the currency trails the amount.
   */
  readonly currency: (options?: CurrencyFactsOptions) => CurrencyFacts;
  /** The date, in the user's field order, punctuation and time zone. */
  readonly date: (value: Date) => string;
  /**
   * The date with its month spelled out, for a heading or a single prominent date:
   * `September 14, 2026`, `14 septembre 2026`. A table column wants {@link FormatBundle.date}.
   */
  readonly dateLong: (value: Date) => string;
  /**
   * A calendar day, in the user's field order but in no zone at all — for a payload that carries
   * `2026-01-15` rather than a moment. Converting a day into the reader's zone is what turns it
   * into the fourteenth for everyone in the Americas.
   *
   * @param value - Midnight UTC on the day to render.
   */
  readonly plainDate: (value: Date, options?: DateFormatOptions) => string;
  /**
   * {@link FormatBundle.dateLong} for a calendar day rather than an instant.
   *
   * @param value - Midnight UTC on the day to render.
   */
  readonly plainDateLong: (value: Date) => string;
  /**
   * The month and day of a calendar day, without the year, for a label too narrow to carry it — a
   * chart axis. The reader's field order with the year dropped, in no zone: `8/4` for a US reader.
   *
   * @param value - Midnight UTC on the day to render.
   */
  readonly plainMonthDay: (value: Date) => string;
  /**
   * The pattern {@link FormatBundle.date} renders by, for a picker that has to display and reparse
   * one. `MM/dd/yyyy` for a US reader, `dd.MM.yyyy` for a German one.
   */
  readonly datePattern: () => string;
  /**
   * The locale this bundle renders in, for a third-party widget that formats on its own account —
   * a calendar naming its months, say — and has to be told which one to use.
   *
   * Not an invitation to format by hand. Everything the bundle already does, it does better here
   * than a call site passing this to `Intl` would.
   */
  readonly locale: () => string;
  /** The time of day, on the clock the user chose. */
  readonly time: (value: Date, options?: DateTimeOptions) => string;
  /**
   * A time of day belonging to no day — a recurring departure, an opening hour, a quiet-hours
   * bound — read from the `08:00` or `14:15:00` the payload carries.
   *
   * Takes the string rather than a `Date` on purpose: a recurring time has no date and no zone, and
   * the anchor instant a caller would have to invent to reach {@link FormatBundle.time} is a fact
   * the data does not contain.
   */
  readonly plainTime: (value: string) => string;
  readonly dateTime: (value: Date, options?: DateTimeOptions) => string;
  /**
   * What the reader's zone is called, for a column that names it in the header instead of on every
   * row. Takes the instant that decides between standard and daylight naming; defaults to now.
   */
  readonly zoneName: (value?: Date) => string;
  /** Both ends of a range of days, joined as the locale joins them: `05/10/2026 – 05/31/2026`. */
  readonly dateRange: (start: Date, end: Date) => string;
  /**
   * `dateRange` for two calendar days rather than two instants — a contract term running
   * `2026-01-15` to `2027-01-15`. Neither end is converted, so neither slips a day.
   *
   * @param start - Midnight UTC on the day the span opens.
   * @param end - Midnight UTC on the day it closes.
   */
  readonly plainDateRange: (start: Date, end: Date) => string;
  /** Both ends of a span of the clock: `8:00 AM – 9:00 PM`, or with the zone named once. */
  readonly timeRange: (
    start: Date,
    end: Date,
    options?: DateTimeOptions
  ) => string;
  /** How long ago, in words: `2 hours ago`, `yesterday`, `now`. */
  readonly relativeTime: (value: Date, now?: Date) => string;
  /**
   * A time of day labelled by the day it falls on: `Today at 8:00 AM`, `Yesterday at 3:00 PM`.
   *
   * The tier between {@link FormatBundle.relativeTime} and {@link FormatBundle.dateTime}, for a
   * reader who wants the wall clock but not the arithmetic. Beyond tomorrow and yesterday the
   * calendar word gives way to the date, so the shape holds for any instant.
   */
  readonly calendarTime: (value: Date, now?: Date) => string;
  /**
   * How recently something happened, on one ladder: `Just Now`, then a count, then the wall clock
   * with its day, then the timestamp.
   *
   * What a last-seen column wants, and what to reach for instead of assembling those four from the
   * members above — the thresholds are part of the design, and a screen that picks its own will
   * drift from every other screen showing the same kind of value.
   */
  readonly recency: (value: Date, now?: Date) => string;
  /** A length of time in seconds, as its two most significant units: `1h 48m` or `1 hour 48 minutes`. */
  readonly duration: (
    totalSeconds: number,
    options?: { style?: 'long' | 'short' | 'narrow' }
  ) => string;
  /**
   * The same length on a clock face: `4:32`, `1:02:33`. What a scrubber, a clip timeline and a
   * countdown show, where {@link FormatBundle.duration}'s `4m 32s` cannot be read at a glance.
   */
  readonly clockDuration: (totalSeconds: number) => string;
  /**
   * A length of time that was written down rather than measured — a contract term, a plan period
   * — given as an ISO-8601 duration and spelled in full: `P39M` reads `3 years, 3 months`.
   *
   * Months and years are why this is not {@link FormatBundle.duration}: a month has no fixed
   * number of seconds, so a term cannot be routed through a formatter that counts them.
   *
   * Two units unless `fields` asks for more.
   */
  readonly isoDuration: (value: string, options?: IsoDurationOptions) => string;
  /** A phone number formatted for the reader, or the input unchanged if it is not a valid number. */
  readonly phone: (value: string) => string;
  /** A postal address on one line. */
  readonly address: (fields: AddressFields) => string;
  /** The same address as the lines an envelope would carry. */
  readonly addressLines: (fields: AddressFields) => string[];
  readonly parse: ParseBundle;
};

const MEASUREMENT_KINDS = Object.keys(UNIT_CODES) as MeasurementKind[];

/**
 * Assembles the bundle for one preference set.
 *
 * A plain function of its argument rather than a hook, so the provider, the imperative snapshot and
 * the tests all build the same thing the same way.
 *
 * @param preferences - The resolved preferences every formatter closes over.
 * @returns The bundle, safe to hold by reference for as long as those preferences hold.
 */
export const buildFormatBundle = (
  preferences: PreferenceValues
): FormatBundle => {
  const formatterFor = <K extends MeasurementKind>(
    kind: K
  ): MeasurementFormatter<K> => {
    const unit = preferences.units[kind];
    const raw = (value: number, valueUnit: UnitCodeFor<K>): number =>
      convert(kind, value, valueUnit, unit);

    return {
      unit,
      format: (value, { valueUnit, context }) =>
        formatMeasurement(preferences, raw(value, valueUnit), {
          kind,
          unit,
          digits: digitsFor(kind, unit, context),
        }),
      raw: (value, { valueUnit }) => raw(value, valueUnit),
      label: () => labelFor(preferences.locale, kind, unit),
      digits: (context) => digitsFor(kind, unit, context),
    };
  };

  const parserFor =
    <K extends MeasurementKind>(kind: K): MeasurementParser<K> =>
    (input, { to }) => {
      const typed = parseNumber(input, preferences.locale);
      if (typed === undefined) return undefined;

      return convert(kind, typed, preferences.units[kind], to);
    };

  // `Object.fromEntries` cannot express that each key's value is indexed by that same key, so the
  // correlation is asserted here and guaranteed by the two builders above being generic in `K`.
  const measurements = Object.fromEntries(
    MEASUREMENT_KINDS.map((kind) => [kind, formatterFor(kind)])
  ) as { [K in MeasurementKind]: MeasurementFormatter<K> };

  const parsers = Object.fromEntries(
    MEASUREMENT_KINDS.map((kind) => [kind, parserFor(kind)])
  ) as { [K in MeasurementKind]: MeasurementParser<K> };

  return {
    ...measurements,
    number: (value, options) => formatNumber(preferences, value, options),
    percent: (value, options) => formatPercent(preferences, value, options),
    money: (value, options) => formatMoney(preferences, value, options),
    currency: (options) => currencyFactsOf(preferences, options),
    date: (value) => formatDate(preferences, value),
    dateLong: (value) => formatDateLong(preferences, value),
    plainDate: (value, options) => formatPlainDate(preferences, value, options),
    plainDateLong: (value) => formatPlainDateLong(preferences, value),
    plainMonthDay: (value) => formatPlainMonthDay(preferences, value),
    datePattern: () => datePatternOf(preferences),
    locale: () => preferences.locale,
    time: (value, options) => formatTime(preferences, value, options),
    plainTime: (value) => formatPlainTime(preferences, value),
    dateTime: (value, options) => formatDateTime(preferences, value, options),
    zoneName: (value) => formatZoneName(preferences, value),
    dateRange: (start, end) => formatDateRange(preferences, start, end),
    plainDateRange: (start, end) =>
      formatPlainDateRange(preferences, start, end),
    timeRange: (start, end, options) =>
      formatTimeRange(preferences, start, end, options),
    relativeTime: (value, now) => formatRelativeTime(preferences, value, now),
    calendarTime: (value, now) => formatCalendarTime(preferences, value, now),
    recency: (value, now) => formatRecency(preferences, value, now),
    duration: (totalSeconds, options) =>
      formatDuration(preferences, totalSeconds, options),
    clockDuration: (totalSeconds) =>
      formatClockDuration(preferences, totalSeconds),
    isoDuration: (value, options) =>
      formatIsoDuration(preferences, value, options),
    phone: (value) => formatPhone(preferences, value),
    address: (fields) => formatAddress(preferences, fields),
    addressLines: (fields) => formatAddressLines(preferences, fields),
    parse: {
      ...parsers,
      number: (input) => parseNumber(input, preferences.locale),
    },
  };
};
